/**
 * Community Garden — Web Client Authentication (RISK-006, GARDEN-142)
 *
 * Handles Discord OAuth flow, secure token storage, and user session management.
 * Uses memory-only token storage to prevent XSS attacks.
 *
 * GARDEN-142: Avatar state now managed by avatar-store (single source of truth).
 */

import { SecureTokenStorage, TokenPair } from './token-storage';
import { avatarStore } from './stores/avatar-store';
import { errorTracker } from './error-tracking.js';
import { analytics } from './analytics.js';

export interface User {
  id: string;
  discord_id: string;
  username: string;
  avatar: string | null;
  discriminator: string | null;
  global_name?: string | null;
  /** Account-wide lifetime total; current-garden rank uses player-progression-store. */
  points: number;
  contributions?: number;
  email?: string | null;
  createdAt?: string;
  lastLogin?: string;
  created_at?: string;
  last_login?: string;
}

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  /** True if the error was caused by user denying OAuth authorization */
  isOAuthDenied?: boolean;
}

/** GARDEN-142: Avatar change event payload */
export interface AvatarChangeEvent {
  avatarUrl: string | null;
  userId: string;
  timestamp: number;
}

// Use relative URL for auth API (same origin as the web app)
const AUTH_API_URL = ''; // Empty string means relative to current origin

// Legacy storage key for user data only (no tokens)
const USER_STORAGE_KEY = 'garden_user';

/* ── storage ─────────────────────────────────────────── */

/**
 * Secure user data storage (no tokens - tokens are memory-only)
 */
class UserStorage {
  static getUser(): User | null {
    try {
      const stored = localStorage.getItem(USER_STORAGE_KEY);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  static setUser(user: User | null): void {
    if (user) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  }

  static clear(): void {
    localStorage.removeItem(USER_STORAGE_KEY);
  }
}

/* ── auth service ───────────────────────────────────── */

export class AuthService {
  private state: AuthState = {
    user: UserStorage.getUser(),
    loading: false,
    error: null
  };
  private listeners: ((state: AuthState) => void)[] = [];
  // GARDEN-142: avatarListeners removed - using avatar-store instead
  private tokenRefreshInterval: number | null = null;

  constructor() {
    // Do NOT auto-validate here.
    // Router.start() performs the authoritative startup validation in a controlled order.
    // Auto-validating in constructor can race initial routing/auth redirects.

    // Set up periodic token refresh
    this.setupTokenRefresh();
  }

  private setupTokenRefresh(): void {
    // Check token every 60 seconds for refresh needs
    this.tokenRefreshInterval = window.setInterval(() => {
      if (SecureTokenStorage.needsRefresh()) {
        SecureTokenStorage.refreshTokens().then(tokenPair => {
          if (!tokenPair && this.state.user) {
            // Token refresh failed but we thought we were logged in
            this.handleTokenExpired();
          }
        });
      }
    }, 60000);
  }

  private cleanup(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  }

  getState(): AuthState {
    return { ...this.state };
  }

  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.push(listener);
    listener(this.state);

    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * GARDEN-142: Subscribe to avatar change events
   * Delegates to avatar-store as single source of truth.
   * Returns unsubscribe function
   */
  onAvatarChange(listener: (event: AvatarChangeEvent) => void): () => void {
    // Subscribe to avatar store events and map to legacy format
    return avatarStore.onAvatarChange((event) => {
      listener({
        avatarUrl: event.discordAvatarUrl,
        userId: event.userId || '',
        timestamp: event.timestamp
      });
    });
  }

  private notify() {
    UserStorage.setUser(this.state.user);
    this.listeners.forEach(listener => listener(this.state));
  }

  private setState(updates: Partial<AuthState>) {
    this.state = { ...this.state, ...updates };
    this.notify();
  }

  /**
   * Handle token expiration gracefully
   */
  private handleTokenExpired(): void {
    SecureTokenStorage.clear();
    this.setState({
      user: null,
      error: 'Your session has expired. Please log in again.'
    });
    this.cleanup();
  }

  // Public method to update auth state (used for OAuth callback)
  updateAuthState(user: User, tokenPair: TokenPair): void {
    SecureTokenStorage.setTokens(tokenPair);

    // GARDEN-142: Initialize avatar store with user ID
    avatarStore.setUserId(user.id);

    // Set initial Discord avatar URL if available
    if (user.avatar) {
      const ext = user.avatar.startsWith('a_') ? 'gif' : 'png';
      const avatarUrl = `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.${ext}`;
      avatarStore.setDiscordAvatarUrl(avatarUrl);
    } else {
      // Clear any stale URL from a previous session (e.g. user removed their avatar)
      avatarStore.setDiscordAvatarUrl(null);
    }

    this.setState({
      user,
      loading: false,
      error: null,
      isOAuthDenied: false
    });
    this.setupTokenRefresh();
  }

  /**
   * GARDEN-157: Handle OAuth denial or error from Discord
   * Called when user denies authorization or OAuth fails
   */
  handleOAuthError(errorCode: string, errorDescription?: string): void {
    let message: string;
    let isDenied = false;

    switch (errorCode) {
      case 'access_denied':
        message = 'You declined the authorization. Your garden awaits when you\'re ready!';
        isDenied = true;
        break;
      case 'server_error':
        message = 'Discord is having trouble. Please try again in a moment.';
        break;
      case 'temporarily_unavailable':
        message = 'Discord is temporarily unavailable. Please try again shortly.';
        break;
      case 'invalid_scope':
        message = 'There was a configuration error. Please contact support.';
        break;
      default:
        message = errorDescription || 'Login failed. Please try again.';
        isDenied = errorCode === 'access_denied';
    }

    this.setState({
      loading: false,
      error: message,
      isOAuthDenied: isDenied
    });
  }

  /**
   * GARDEN-157: Check URL for OAuth error parameters
   * Returns true if an error was handled
   */
  checkForOAuthError(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      this.handleOAuthError(error, errorDescription || undefined);

      // Clean URL - remove error parameters
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);

      return true;
    }

    return false;
  }

  /**
   * GARDEN-157: Clear any auth error state and allow retry
   */
  clearError(): void {
    this.setState({
      error: null,
      isOAuthDenied: false
    });
  }

  async loginWithDiscord(): Promise<void> {
    this.setState({ loading: true, error: null, isOAuthDenied: false });

    try {
      // GARDEN-157: Check if window.open is blocked (popup blocker)
      const testWindow = window.open('', '_blank');
      if (testWindow === null || testWindow.closed || typeof testWindow.closed === 'undefined') {
        // Popup blocked - use full redirect instead
        console.log('[Auth] Popup blocked, using redirect flow');
      } else {
        // Close test window
        testWindow.close();
      }

      // Redirect to Discord OAuth endpoint (server will redirect to Discord)
      window.location.href = `${AUTH_API_URL}/auth/discord`;
    } catch (error: any) {
      // GARDEN-157: Handle network errors or unexpected issues
      const errorMessage = error.message || 'Login failed';
      const isNetworkError = errorMessage.includes('network') ||
                            errorMessage.includes('fetch') ||
                            errorMessage.includes('Failed to fetch');

      this.setState({
        loading: false,
        error: isNetworkError
          ? 'Network error. Please check your connection and try again.'
          : errorMessage
      });
      throw error;
    }
  }

  async logout(): Promise<void> {
    // Revoke tokens server-side and clear locally
    await SecureTokenStorage.revokeTokens();

    this.setState({
      user: null,
      loading: false,
      error: null
    });

    // GARDEN-142: Reset avatar store on logout
    avatarStore.reset();

    // GARDEN-210: Clear user context in error tracking and analytics on logout
    // This prevents errors after logout from being associated with the previous user
    errorTracker.setContext(null, null);
    analytics.clearUser();

    UserStorage.clear();
    this.cleanup();
  }

  async validateSession(): Promise<boolean> {
    // Seed state from persisted user when available, but do not require it.
    // A valid refresh/session cookie can rehydrate auth even without local user data
    // (e.g. clean browser contexts used by visual QA screenshot capture).
    const persistedUser = UserStorage.getUser();
    if (persistedUser && !this.state.user) {
      this.setState({ user: persistedUser });
    }

    // Check if we have a valid token or can refresh via cookie-backed session
    const validToken = await SecureTokenStorage.getValidToken();

    if (!validToken) {
      // No valid token and refresh failed
      this.setState({ user: null });
      UserStorage.clear();
      return false;
    }

    // Token is valid, refresh user data from server
    await this.fetchUser();
    return !!this.state.user;
  }

  async fetchUser(): Promise<void> {
    const token = await SecureTokenStorage.getValidToken();

    if (!token) {
      this.handleTokenExpired();
      return;
    }

    try {
      const response = await fetch(`${AUTH_API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.user) {
        // GARDEN-142: Sync Discord avatar with avatar store
        avatarStore.setUserId(data.user.id);
        if (data.user.avatar) {
          const ext = data.user.avatar.startsWith('a_') ? 'gif' : 'png';
          const avatarUrl = `https://cdn.discordapp.com/avatars/${data.user.discord_id}/${data.user.avatar}.${ext}`;
          avatarStore.setDiscordAvatarUrl(avatarUrl);
        } else {
          // Clear any stale URL from a previous session (e.g. user removed their avatar)
          avatarStore.setDiscordAvatarUrl(null);
        }

        this.setState({ user: data.user });
      } else if (response.status === 401) {
        // Token rejected by server
        this.handleTokenExpired();
      } else {
        await this.logout();
      }
    } catch (error) {
      console.warn('Failed to fetch user:', error);
    }
  }

  /**
   * Get auth headers with valid token.
   * Automatically refreshes token if needed.
   */
  async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await SecureTokenStorage.getValidToken();

    if (!token) {
      return {};
    }

    return {
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Synchronous version - use only when token is known to be valid
   * @deprecated Use getAuthHeaders() instead for automatic refresh
   */
  getAuthHeadersSync(): Record<string, string> {
    const token = SecureTokenStorage.getAccessToken();

    if (!token) {
      return {};
    }

    return {
      'Authorization': `Bearer ${token}`
    };
  }

  isAuthenticated(): boolean {
    return !!SecureTokenStorage.getAccessToken() && !!this.state.user;
  }

  /** Return the current logged-in user object, or null. */
  getCurrentUser(): User | null {
    return this.state.user;
  }

  /**
   * Check if session is valid (including token expiry)
   */
  async checkSession(): Promise<boolean> {
    const token = await SecureTokenStorage.getValidToken();
    return !!token && !!this.state.user;
  }

  getAvatarUrl(size: number = 64): string | null {
    const { user } = this.state;
    if (!user) return null;

    // Prefer current user avatar hash from auth state
    if (user.avatar) {
      const ext = user.avatar.startsWith('a_') ? 'gif' : 'png';
      return `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.${ext}?size=${size}`;
    }

    // Fallback to avatar-store (legacy compatibility)
    const storeUrl = avatarStore.getDiscordAvatarUrl();
    if (storeUrl) {
      return storeUrl.includes('?') ? `${storeUrl}&size=${size}` : `${storeUrl}?size=${size}`;
    }

    // Discord default avatar
    const defaultIndex = parseInt(user.discriminator || '0', 10) % 5;
    return `https://cdn.discordapp.com/embed/avatars/${defaultIndex}.png`;
  }

  getDisplayName(): string {
    const { user } = this.state;

    if (!user) {
      return 'Guest';
    }

    return user.global_name || user.username;
  }

  updateAccountPoints(points: number): void {
    const currentState = this.getState();
    if (currentState.user) {
      // Create a new user object to trigger proper change detection
      const updatedUser = { ...currentState.user, points };
      this.setState({ user: updatedUser });
    }
  }

  /** @deprecated Use updateAccountPoints; garden-scoped points do not belong in auth state. */
  updateUserPoints(points: number): void {
    this.updateAccountPoints(points);
  }

  /**
   * GARDEN-142: Update user Discord avatar from WebSocket or external source
   * Delegates to avatar-store as single source of truth.
   */
  updateAvatar(avatarHash: string | null): void {
    const currentState = this.getState();
    if (!currentState.user) return;

    // Build Discord avatar URL
    let avatarUrl: string | null = null;
    if (avatarHash) {
      const ext = avatarHash.startsWith('a_') ? 'gif' : 'png';
      avatarUrl = `https://cdn.discordapp.com/avatars/${currentState.user.discord_id}/${avatarHash}.${ext}`;
    }

    // Update avatar store (single source of truth)
    avatarStore.setDiscordAvatarUrl(avatarUrl);

    // Update local user reference with latest avatar hash
    if (currentState.user.avatar !== avatarHash) {
      const updatedUser = { ...currentState.user, avatar: avatarHash };
      this.setState({ user: updatedUser });
    }
  }

  /**
   * GARDEN-158: Check if user has a specific permission bitmask
   * @param permissions - Permission string from guild (bitmask as string)
   * @param flag - Permission flag to check (e.g., 0x20 for MANAGE_GUILD)
   * @returns true if user has the permission
   */
  hasPermission(permissions: string | number | undefined, flag: number): boolean {
    if (permissions === undefined || permissions === null) return false;
    const perms = typeof permissions === 'string' ? parseInt(permissions, 10) : permissions;
    if (isNaN(perms)) return false;
    return (perms & flag) !== 0;
  }
}

// Singleton instance
export const authService = new AuthService();
